import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Type definitions
export interface Project {
  id: string;
  name: string;
  homeFolder: string;
  description: string;
}

export interface ProjectStore {
  projects: Project[];
  activeProject: Project | null;
  addProject: (project: Project) => Promise<void>;
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  setActiveProject: (id: string) => void;
}

// Placeholder projects - will be replaced with API calls later
const initialProjects: Project[] = [
  {
    id: '1',
    name: 'project_1',
    homeFolder: '/projects/project_1',
    description: 'First demo project',
  },
  {
    id: '2',
    name: 'project_2',
    homeFolder: '/projects/project_2',
    description: 'Second demo project',
  },
  {
    id: '3',
    name: 'project_3',
    homeFolder: '/projects/project_3',
    description: 'Third demo project',
  },
];

// const createPlaceholderPipelines = () => {
//     // Project 2 pipelines
//     for (let i = 1; i <= 4; i++) {
//         localStorage.setItem(`project_2_pipeline_${i}`, JSON.stringify({
//             id: `project_2_pipeline_${i}`,
//             name: `Project 2 Pipeline ${i}`,
//             nodes: [],
//             edges: [],
//             project: '2'
//         }));
//     }

//     // Project 3 pipelines
//     for (let i = 1; i <= 5; i++) {
//         localStorage.setItem(`project_3_pipeline_${i}`, JSON.stringify({
//             id: `project_3_pipeline_${i}`,
//             name: `Project 3 Pipeline ${i}`,
//             nodes: [],
//             edges: [],
//             project: '3'
//         }));
//     }

//     // Ensure existing pipelines are assigned to project 1
//     for (let i = 0; i < localStorage.length; i++) {
//         const key = localStorage.key(i);
//         if (key.startsWith('pipeline_') && !key.startsWith('project_')) {
//             const pipeline = JSON.parse(localStorage.getItem(key));
//             pipeline.project = '1';
//             localStorage.setItem(key, JSON.stringify(pipeline));
//         }
//     }
// };

// Create placeholder pipelines on store initialization
// createPlaceholderPipelines();

const useProjectStore = create<ProjectStore>()(
  persist(
    (set, get) => ({
      // Initialize with placeholder projects
      projects: initialProjects,
      activeProject: null,

      // These functions will be updated to use API calls later
      addProject: async (project: Project): Promise<void> => {
        // TODO: Replace with API call
        // const response = await fetch('/api/projects', {
        //     method: 'POST',
        //     headers: { 'Content-Type': 'application/json' },
        //     body: JSON.stringify(project)
        // });
        // const newProject = await response.json();

        const { projects } = get();
        if (projects.some((p) => p.name === project.name)) {
          throw new Error('Project name must be unique');
        }
        if (projects.some((p) => p.homeFolder === project.homeFolder)) {
          throw new Error('Project home folder must be unique');
        }
        set((state) => ({
          projects: [...state.projects, project],
        }));
      },

      updateProject: async (id: string, updates: Partial<Project>): Promise<void> => {
        // TODO: Replace with API call
        // await fetch(`/api/projects/${id}`, {
        //     method: 'PUT',
        //     headers: { 'Content-Type': 'application/json' },
        //     body: JSON.stringify(updates)
        // });

        const { projects } = get();
        if (updates.name && projects.some((p) => p.id !== id && p.name === updates.name)) {
          throw new Error('Project name must be unique');
        }
        if (updates.homeFolder && projects.some((p) => p.id !== id && p.homeFolder === updates.homeFolder)) {
          throw new Error('Project home folder must be unique');
        }
        set((state) => ({
          projects: state.projects.map((p) => (p.id === id ? { ...p, ...updates } : p)),
        }));
      },

      deleteProject: async (id: string): Promise<void> => {
        // TODO: Replace with API call
        // await fetch(`/api/projects/${id}`, {
        //     method: 'DELETE'
        // });

        set((state) => ({
          projects: state.projects.filter((p) => p.id !== id),
          activeProject: state.activeProject?.id === id ? null : state.activeProject,
        }));
      },

      setActiveProject: (id: string): void => {
        const project = get().projects.find((p) => p.id === id);
        set({ activeProject: project || null });

        // Clear existing pipelines from store
        localStorage.setItem('activePipelineId', 'null');

        // Trigger pipeline reload in HopStore
        const event = new CustomEvent('projectChanged', {
          detail: { projectId: id },
        });
        window.dispatchEvent(event);
      },
    }),
    {
      name: 'hop-project-storage',
    }
  )
);

export default useProjectStore;
